import router, { leavePage } from './router'
import store from './store'
import qs from 'qs'
import { fetchAllPages } from './plugins/paginate'
import { shrinkPhotos } from './plugins/photoScale'

const request = (method, rawUrl, queryParams, bodyParams, { json = true, handleErrors = true } = {}) => {
  const url = new URL(rawUrl + '?' + qs.stringify(queryParams), document.baseURI)
  
  let response = false
  const headers = {}

  if (json) {
    headers['Content-Type'] ='application/json'
  }

  // The page this request was made for. A missing-page answer only backs out of
  // that page: if the user has already moved on — a delete sending them to the
  // list while a tab still refetches the record it just removed — going back
  // would return them to the page they left.
  const startedOn = router.currentRoute.value.fullPath

  return fetch(url, {
      method,
      body: bodyParams && json ? JSON.stringify(bodyParams) : bodyParams,
      credentials: 'include',
      headers,
      // Reads only: a list on a dead connection is given up on after a minute.
      // Writes are left to finish — some (a Koondarve, a restore, a backup
      // upload) rightly take longer, and must not be reported as failed while
      // the server is still doing them.
      signal: (method === 'GET' && typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') ? AbortSignal.timeout(60 * 1000) : undefined
    }).catch(() => {
      // No answer at all. The browser's own words for it are English and
      // differ by browser ("Failed to fetch", "Load failed").
      throw { message: 'Ühendus puudub või on liiga aeglane — proovi uuesti', network: true }
    }).then(res => {
      response = res
      return res.text()
    }).then(text => {
      let data
      try {
        data = text ? JSON.parse(text) : {}
      } catch (e) {
        data = { message: 'Serveri viga' }
      }
      if (!response.ok) {
        // Pääsuvõtmed Kohustuslik switched on while this session had no key.
        if (response.status === 403 && data && data.code === 'PASSKEY_REQUIRED') {
          store.commit('setMustAddPasskey', true)
          if (router.currentRoute.value.name !== 'passkey-required') {
            router.push({ name: 'passkey-required', query: { to: router.currentRoute.value.fullPath } }).catch(() => {})
          }
          throw data
        }
        if (handleErrors && response.status === 401) {
          const to = router.currentRoute.value.path
          if (to !== '/login') {
            router.push('/login?to=' + encodeURIComponent(to)).catch(() => {})
          }
        } else if (handleErrors && [403, 404].includes(response.status) && method === 'GET' &&
          router.currentRoute.value.fullPath === startedOn) {
          // Deep-linked into a forbidden/missing page: notify and back out.
          // Action requests (POST/PUT/DELETE) rethrow instead — the calling
          // component shows the error, and navigating away mid-action (plus a
          // second toast) would be disruptive. A request left over from a page
          // the user has already left rethrows too (see startedOn).
          store.dispatch('showNotification', {
            text: data.message,
            color: 'red'
          })
          leavePage()
        }
        // The status, for a caller that has to tell a 401 from a 404. Not
        // enumerable, so nothing that copies or prints the answer sees it.
        if (data && typeof data === 'object' && !('status' in data)) {
          Object.defineProperty(data, 'status', { value: response.status })
        }
        throw data
      }
      return data
    })
}

const get = (rawUrl, queryParams = {}, opts = {}) => {
  return request('GET', rawUrl, queryParams, null, opts)
}

const post = (rawUrl, bodyParams = {}, opts = {}) => {
  return request('POST', rawUrl, {}, bodyParams, opts)
}

const put = (rawUrl, bodyParams = {}) => {
  return request('PUT', rawUrl, {}, bodyParams)
}

const del = (rawUrl, bodyParams = {}) => {
  return request('DELETE', rawUrl, {}, bodyParams)
}

const upload = async (rawUrl, files) => {
  const data = new FormData()

  // Shrunk to what the server keeps before it leaves the device — see
  // plugins/photoScale. The originals are several megabytes each and none of
  // that survives the resize on the other end.
  const photos = await shrinkPhotos(files)
  photos.forEach(({ blob, name }) => {
    data.append('file', blob, name)
  })

  return request('POST', rawUrl, {}, data, { json: false })
}

// A multipart POST through XMLHttpRequest, for its upload progress — fetch has
// none. An answer that is not JSON is a proxy's page (Cloudflare's 413, a 502)
// and is named by its status rather than passed off as a server error.
const sendForm = (rawUrl, form, onProgress) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest()
  xhr.open('POST', new URL(rawUrl, document.baseURI).href)
  xhr.withCredentials = true
  xhr.upload.onprogress = e => { if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total) }
  xhr.onload = () => {
    let data
    try {
      data = xhr.responseText ? JSON.parse(xhr.responseText) : {}
    } catch (e) {
      data = { message: xhr.status === 413 ? 'Päring on liiga suur (413)' : 'Serveri viga (' + xhr.status + ')' }
    }
    if (xhr.status >= 200 && xhr.status < 300) return resolve(data)
    Object.defineProperty(data, 'status', { value: xhr.status })
    reject(data)
  }
  xhr.onerror = () => reject({ message: 'Ühendus katkes' })
  xhr.send(form)
})

// crypto.randomUUID exists only in a secure context; intra is also opened over
// plain http on the LAN. A v4 uuid from getRandomValues works in both.
const uuid = () => {
  const b = crypto.getRandomValues(new Uint8Array(16))
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('')
  return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20)
}

const db = {}

const cache = async (key, getter, ttl = 60 * 1000, force = false) => {
  let hit = db[key]

  if (!hit || hit.created + ttl < Date.now() || force) {
    db[key] = hit = {
      created: Date.now(),
      value: await getter()
    }
  }

  return hit.value
}

class Api {
  getUsers (queryParams) {
    return get('/api/user', queryParams)
  }
  getHistory (queryParams) {
    return get('/api/history', queryParams)
  }
  getTechnics (queryParams) {
    return get('/api/user/technic', queryParams)
  }
  getPlannerUsers (queryParams) {
    return get('/api/user/planner', queryParams)
  }
  getReminderLog (queryParams) {
    return get('/api/system/reminders', queryParams)
  }
  getUpcomingReminders (queryParams) {
    return get('/api/system/reminders/upcoming', queryParams)
  }
  // Süsteem → SMS. Both answer Twilio's own refusal as a 502 the page shows in
  // place, so neither may navigate away.
  getSmsBalance () {
    return get('/api/system/sms/balance', {}, { handleErrors: false })
  }
  getSmsList (queryParams) {
    return get('/api/system/sms', queryParams, { handleErrors: false })
  }
  // Süsteem → E-post (Resend). Read-only at Resend; a missing or send-only key
  // is a 502 the page shows in place, so none of these navigate away.
  getMailHealth () {
    return get('/api/system/mail/health', {}, { handleErrors: false })
  }
  getMailMetrics (queryParams) {
    return get('/api/system/mail/metrics', queryParams, { handleErrors: false })
  }
  getMailList (queryParams) {
    return get('/api/system/mail', queryParams, { handleErrors: false })
  }
  getMail (id) {
    return get('/api/system/mail/' + encodeURIComponent(id), {}, { handleErrors: false })
  }
  getMailBlocked (queryParams) {
    return get('/api/system/mail/blocked', queryParams, { handleErrors: false })
  }
  getMailLimits () {
    return get('/api/system/mail/limits', {}, { handleErrors: false })
  }
  updateMailLimits (bodyParams) {
    return put('/api/system/mail/limits', bodyParams)
  }
  getMailProvider () {
    return get('/api/system/mail/provider', {}, { handleErrors: false })
  }
  updateMailProvider (bodyParams) {
    return put('/api/system/mail/provider', bodyParams)
  }
  getMailSenders () {
    return get('/api/system/mail/senders', {}, { handleErrors: false })
  }
  getSmsProvider () {
    return get('/api/system/sms/provider', {}, { handleErrors: false })
  }
  updateSmsProvider (provider) {
    return put('/api/system/sms/provider', { provider })
  }
  // Seaded -> Ladu: where Ladu is and its key (never read back), a harmless
  // call to try them, and which van is whose.
  getLaduSettings () {
    return get('/api/system/ladu', {}, { handleErrors: false })
  }
  updateLaduSettings (body) {
    return put('/api/system/ladu', body)
  }
  testLadu () {
    return post('/api/system/ladu/test', {})
  }
  getLaduVans () {
    return get('/api/system/ladu/vans', {}, { handleErrors: false })
  }
  updateLaduVan (technicId, storeId) {
    return put('/api/system/ladu/vans', { technicId, storeId })
  }
  getSystemSettings () {
    return get('/api/system/settings')
  }
  async updateSystemSettings (bodyParams) {
    const result = await put('/api/system/settings', bodyParams)
    // A module that was just switched on must not stay invisible in the menu
    // until the features cache below expires.
    delete db.features
    return result
  }
  getPortalSettings () {
    return get('/api/system/portal')
  }
  // Intra's own login look, read by the login page before anyone is logged in.
  // Quiet: a failure only means the original look is drawn.
  getIntraLook () {
    return get('/api/site/intra-look', {}, { handleErrors: false })
  }
  // The caller's own menu layout in the new frame (position, hidden). Quiet:
  // a failure only means the default layout is drawn, and the frame says so.
  getMyLayout () {
    return get('/api/site/layout', {}, { handleErrors: false })
  }
  updateMyLayout (bodyParams) {
    return request('PUT', '/api/site/layout', {}, bodyParams, { handleErrors: false })
  }
  // The caller's own settings (Minu seaded): { calendarLook }. Quiet like the
  // layout: a failure only means the Seaded defaults are drawn.
  getMyPrefs () {
    return get('/api/site/prefs', {}, { handleErrors: false })
  }
  updateMyPrefs (bodyParams) {
    return request('PUT', '/api/site/prefs', {}, bodyParams, { handleErrors: false })
  }
  // Süsteem → Seaded → Intra sisselogimine.
  getIntraLookSettings () {
    return get('/api/system/intra-look')
  }
  updateIntraLookSettings (bodyParams) {
    return put('/api/system/intra-look', bodyParams)
  }
  updatePortalSettings (bodyParams) {
    return put('/api/system/portal', bodyParams)
  }
  backupUrl (queryParams = {}) {
    return new URL('/api/system/backup?' + qs.stringify(queryParams), document.baseURI).href
  }
  getBackupEstimate () {
    return get('/api/system/backup/estimate')
  }
  // Polled while a download runs; a failed poll must not navigate the page away.
  getBackupProgress (token) {
    return get('/api/system/backup/progress', { token }, { handleErrors: false })
  }
  // Sends the backup zip in pieces, then starts the restore. In pieces because
  // Cloudflare, in front of the demo, refuses any request body over 100 MB with
  // a page of its own — a whole backup with its files is bigger than that.
  // onProgress(sent, total) follows the bytes across all of them. Rejects with
  // { message } like the other calls do.
  async restoreBackup (file, fields, onProgress) {
    const CHUNK = 25 * 1024 * 1024
    const uploadId = uuid()
    for (let index = 0, offset = 0; offset < file.size || index === 0; index++, offset += CHUNK) {
      const piece = new FormData()
      piece.append('uploadId', uploadId)
      piece.append('index', String(index))
      piece.append('file', file.slice(offset, offset + CHUNK), 'osa-' + index)
      await sendForm('/api/system/restore/chunk', piece, loaded => {
        if (onProgress) onProgress(Math.min(file.size, offset + loaded), file.size)
      })
    }
    const start = new FormData()
    start.append('uploadId', uploadId)
    for (const [k, v] of Object.entries(fields)) start.append(k, String(v))
    return sendForm('/api/system/restore', start)
  }
  // Polled through a restore, which replaces the sessions table — a 401 here
  // is for the page to explain, not a bounce to the login.
  getRestoreJob (id) {
    return get('/api/system/restore/' + id, {}, { handleErrors: false })
  }
  // Automaatne varundus and the Synology that collects its copies.
  getAutoBackup () {
    return get('/api/system/backup/auto')
  }
  saveAutoBackup (bodyParams) {
    return put('/api/system/backup/auto', bodyParams)
  }
  runAutoBackup (full) {
    return post('/api/system/backup/auto/run', { full })
  }
  createBackupToken () {
    return post('/api/system/backup/auto/token')
  }
  revokeBackupToken () {
    return del('/api/system/backup/auto/token')
  }
  autoBackupFileUrl (name) {
    return new URL('/api/system/backup/auto/file/' + encodeURIComponent(name), document.baseURI).href
  }
  restoreSafetyUrl (name) {
    return new URL('/api/system/restore/safety/' + encodeURIComponent(name), document.baseURI).href
  }
  getUser (id) {
    return get('/api/user/' + id)
  }
  createUser (bodyParams) {
    return post('/api/user', bodyParams)
  }
  updateUser (id, bodyParams) {
    return put('/api/user/' + id, bodyParams)
  }
  deleteUser (id) {
    return del('/api/user/' + id)
  }
  unlockUser (id) {
    return post('/api/user/' + id + '/unlock')
  }
  getRoles () {
    return get('/api/role')
  }
  getEvents (queryParams) {
    return get('/api/calendar', queryParams)
  }
  getEvent (id) {
    return get('/api/calendar/' + id)
  }
  deleteEvent (id, body) {
    return del('/api/calendar/' + id, body)
  }
  createEvent (body) {
    return post('/api/calendar', body)
  }
  updateEvent (id, body) {
    return put('/api/calendar/' + id, body)
  }
  updateEventTime (id, body) {
    return put('/api/calendar/' + id + '/time', body)
  }
  getObjects (queryParams) {
    return get('/api/object', queryParams)
  }
  // The numbers over the object list's new look.
  getObjectSummary () {
    return get('/api/object/summary', {}, { handleErrors: false })
  }
  getObject (id) {
    return get('/api/object/' + id)
  }
  // Tab counts, hooldus and "Viimati siin" for the object page's new look.
  // handleErrors off: a 404 here must not send the reader back a page.
  getObjectOverview (id) {
    return get('/api/object/' + id + '/overview', {}, { handleErrors: false })
  }
  createObject (body) {
    return post('/api/object', body)
  }
  updateObject (id, body) {
    return put('/api/object/' + id, body)
  }
  deletObject (id) {
    return del('/api/object/' + id)
  }
  searchObjects (queryParams) {
    return get('/api/object/search', queryParams)
  }
  getDuplicateObjects (queryParams) {
    return get('/api/object/duplicates', queryParams)
  }
  mergeObjects (body) {
    return post('/api/object/merge', body)
  }
  // "Pole duplikaat" on Otsi duplikaate: these objects are different places.
  markNotDuplicate (ids) {
    return post('/api/object/not-duplicate', { ids })
  }
  // The groups marked "Pole duplikaat", and "Taasta" to forget them again.
  getHiddenDuplicates () {
    return get('/api/object/not-duplicate', {}, { handleErrors: false })
  }
  restoreDuplicate (ids) {
    return del('/api/object/not-duplicate', { ids })
  }
  // Ühenda käsitsi: the picked objects, counted the way a found group is.
  getMergePreview (ids) {
    return get('/api/object/merge-preview', { ids: ids.join(',') }, { handleErrors: false })
  }
  // Whether an object already exists at this address (the new look's form
  // asks as soon as one is picked). handleErrors off: a 404 must not navigate.
  checkObjectAddress (queryParams) {
    return get('/api/object/address-check', queryParams, { handleErrors: false })
  }
  searchAdsAddresses (queryParams) {
    return get('/api/site/ads', queryParams)
  }
  getObjectIntervals (id, queryParams) {
    return get('/api/object/' + id + '/interval', queryParams)
  }
  deleteObjectInterval (id, objectId) {
    return del('/api/object/' + objectId + '/interval/' + id)
  }
  getObjectNotes (id, queryParams) {
    return get('/api/object/' + id + '/note', queryParams)
  }
  // Fetch *every* note matching the filter, paging past the server's per-request
  // limit cap (the note endpoint clamps `limit` to 200). Used by the printable
  // history, which is one document rather than a page of one.
  async getAllObjectNotes (id, queryParams = {}) {
    const { items, count } = await fetchAllPages(
      (limit, offset) => this.getObjectNotes(id, { ...queryParams, limit, offset })
        .then(page => ({ items: page.notes, count: page.count })),
      { pageSize: 200 }
    )
    return { notes: items, count }
  }
  // Seadmed: the equipment installed at an address. Active only unless asked
  // otherwise — the Seadmed page is the one screen that also shows what was
  // archived.
  getObjectDevices (objectId, queryParams = {}) {
    return get('/api/object/' + objectId + '/device', queryParams)
  }
  createObjectDevice (objectId, body) {
    return post('/api/object/' + objectId + '/device', body)
  }
  updateObjectDevice (objectId, deviceId, body) {
    return put('/api/object/' + objectId + '/device/' + deviceId, body)
  }
  deleteObjectDevice (objectId, deviceId) {
    return del('/api/object/' + objectId + '/device/' + deviceId)
  }
  // Replaced, not removed: it leaves every list that says what is installed
  // now and stays on the Seadmed page, where it can be brought back.
  archiveObjectDevice (objectId, deviceId) {
    return post('/api/object/' + objectId + '/device/' + deviceId + '/archive')
  }
  restoreObjectDevice (objectId, deviceId) {
    return post('/api/object/' + objectId + '/device/' + deviceId + '/restore')
  }
  // The acts filed against one address. Served by the akt module, so this 404s
  // when the module is off — the tab that asks is hidden in the same case.
  getObjectAkts (id, queryParams) {
    return get('/api/akt', { objectId: id, ...queryParams })
  }
  // Tegemata aktid (`part: 'visits'` — maintenance visits nobody has filed an
  // act for) and Lõpetamata aktid (`part: 'started'` — acts begun and not
  // finished), for the dashboard cards. One part per call: the visits half
  // joins the whole calendar, so asking for both would pay for both.
  //
  // handleErrors off, and this is the point of it: the module is optional, so
  // the route is not mounted at all when it is off and a GET that 404s
  // otherwise toasts and backs out (leavePage) — a dashboard card would throw
  // the reader off the dashboard. A refusal here just leaves the card out.
  getUndoneAkts (queryParams) {
    return get('/api/akt/undone', queryParams, { handleErrors: false })
  }
  createNote (id, body) {
    return post('/api/object/' + id + '/note', body)
  }
  uploadNotePhotos (objectId, noteId, files) {
    return upload('/api/object/' + objectId + '/note/' + noteId + '/photo', files)
  }
  setNoteVisibility (objectId, noteId, body) {
    return put('/api/object/' + objectId + '/note/' + noteId + '/visibility', body)
  }
  // The hooldusakt for one maintenance entry. A URL rather than a fetch: the
  // server sends it inline and the browser's own PDF viewer is the print
  // dialog, same as the akt PDF.
  hooldusaktUrl (objectId, noteId) {
    return new URL('/api/object/' + objectId + '/note/' + noteId + '/hooldusakt/pdf', document.baseURI).href
  }
  sendHooldusakt (objectId, noteId, email) {
    return post('/api/object/' + objectId + '/note/' + noteId + '/hooldusakt/email', { email })
  }
  setWorkOrderVisibility (objectId, calendarId, body) {
    return put('/api/object/' + objectId + '/workorder/' + calendarId + '/visibility', body)
  }
  getObjectPhotos (id) {
    return get('/api/object/' + id + '/photos')
  }
  deleteObjectPhoto (objectId, noteId, picture) {
    return del('/api/object/' + objectId + '/note/' + noteId + '/photo', { picture })
  }
  // Root only. Answers { deleted: 'erased' | 'hidden' } — a märkus whose photos
  // an act still lists is hidden rather than erased, because the two share the
  // files rather than each holding a copy.
  deleteNote (objectId, noteId) {
    return del('/api/object/' + objectId + '/note/' + noteId)
  }
  getClients (queryParams) {
    return get('/api/client', queryParams)
  }
  getClient (id) {
    return get('/api/client/' + id)
  }
  createClient (body) {
    return post('/api/client', body)
  }
  updateClient (id, body) {
    return put('/api/client/' + id, body)
  }
  deleteClient (id) {
    return del('/api/client/' + id)
  }
  // The numbers over the new look's Kliendid list (one type or all).
  // Lisa uus klient (new look): clients that already have this phone, e-mail or name.
  checkClient (queryParams) {
    return get('/api/client/check', queryParams)
  }
  getClientSummary (queryParams) {
    return get('/api/client/summary', queryParams)
  }
  searchClients (queryParams) {
    return get('/api/client/search', queryParams)
  }
  getDuplicateClients (queryParams) {
    return get('/api/client/duplicates', queryParams)
  }
  // Otsi duplikaate in the new look (Kliendid uues kujunduses).
  getClientDuplicateGroups (queryParams) {
    return get('/api/client/duplicate-groups', queryParams)
  }
  getClientMergePreview (ids) {
    return get('/api/client/merge-preview', { ids: ids.join(',') })
  }
  getClientNotDuplicates () {
    return get('/api/client/not-duplicate')
  }
  markClientsNotDuplicate (ids) {
    return post('/api/client/not-duplicate', { ids })
  }
  restoreClientDuplicates (ids) {
    return del('/api/client/not-duplicate', { ids })
  }
  mergeClients (body) {
    return post('/api/client/merge', body)
  }
  // Kliendiportaal lock state on the client page (admin) and "Ava lukk".
  // Süsteem → Turvalisus (root): portal locks, blocks, latest attempts.
  getSystemSecurity () {
    return get('/api/system/security', {}, { handleErrors: false })
  }
  unlockSystemSecurity (target) {
    return post('/api/system/security/unlock', target)
  }
  // Süsteem → Turvalisus → Pääsuvõtmed (root): the switches and who has a key.
  getPasskeySettings () {
    return get('/api/system/security/passkeys', {}, { handleErrors: false })
  }
  savePasskeySettings (change) {
    return put('/api/system/security/passkeys', change)
  }
  // Süsteem → Turvalisus → Lekkinud paroolid (root): the switch and who is listed.
  getPwnedPasswords () {
    return get('/api/system/security/passwords', {}, { handleErrors: false })
  }
  savePwnedPasswords (on) {
    return put('/api/system/security/passwords', { on })
  }
  // Someone else's keys: a user's (admin) or a portal client's (admin, secretary).
  getPasskeysOf (kind, id) {
    return get('/api/' + kind + '/' + id + '/passkeys', {}, { handleErrors: false })
  }
  deletePasskeysOf (kind, id, keyId = null) {
    return del('/api/' + kind + '/' + id + '/passkeys' + (keyId ? '/' + keyId : ''))
  }
  // Süsteem → Andmete korrastamine (root): kind is 'city' or 'country'.
  getSystemData (kind) {
    return get('/api/system/data/' + kind)
  }
  mergeSystemData (kind, body) {
    return post('/api/system/data/' + kind + '/merge', body)
  }
  renameSystemData (kind, id, title) {
    return put('/api/system/data/' + kind + '/' + id, { title })
  }
  deleteSystemData (kind, id) {
    return del('/api/system/data/' + kind + '/' + id)
  }
  // Süsteem → Statistika (root): one small row per töökäsk of the period, and
  // the töökäsud behind a number.
  getStats (queryParams) {
    return get('/api/system/stats', queryParams)
  }
  getStatsList (queryParams) {
    return get('/api/system/stats/list', queryParams)
  }
  // Süsteem → Andmete korrastamine → Maksmata tööd (root).
  getUnpaidSummary (queryParams) {
    return get('/api/system/unpaid/summary', queryParams)
  }
  getUnpaid (queryParams) {
    return get('/api/system/unpaid', queryParams)
  }
  getUnpaidLog () {
    return get('/api/system/unpaid/log')
  }
  settleUnpaid (body) {
    return post('/api/system/unpaid/settle', body)
  }
  undoUnpaid (id) {
    return post('/api/system/unpaid/undo/' + id)
  }
  getClientPortalAccess (id) {
    return get('/api/client/' + id + '/portal-access', {}, { handleErrors: false })
  }
  unlockClientPortal (id) {
    return post('/api/client/' + id + '/portal-unlock')
  }
  getClientOverview (id) {
    return get('/api/client/' + id + '/overview', {}, { handleErrors: false })
  }
  getRelatedClients (id) {
    return get('/api/client/' + id + '/related/client')
  }
  getPlanner (queryParams) {
    return get('/api/planner', queryParams)
  }
  addToPlanner (body) {
    return put('/api/planner/add', body)
  }
  getPlannerMonth (queryParams) {
    return get('/api/planner/month', queryParams)
  }
  getWorkdays () {
    return get('/api/planner/workdays')
  }
  // Töökäsud per technician and day, for the new Planeerija.
  getPlannerWork (queryParams) {
    return get('/api/planner/work', queryParams)
  }
  getNotices () {
    return get('/api/site/notices')
  }
  // Which optional modules this deployment runs, e.g. { akt: true }. Root can
  // switch these from Süsteem -> Seaded, so the cache is short: the tab that
  // did the switching is updated at once (updateSystemSettings drops this
  // entry), and everyone else's follows within a few minutes.
  getFeatures () {
    return cache('features', () => get('/api/site/features'), 5 * 60 * 1000)
  }
  // The commit the server runs (admin and root): the tag on the Süsteem pages.
  getVersion () {
    return cache('version', () => get('/api/site/version', {}, { handleErrors: false }), 5 * 60 * 1000)
  }
  getCountries () {
    return cache('countries', () => get('/api/site/country'), 60 * 1000)
  }
  getCities (countryId) {
    const url = '/api/site/country/' + countryId + '/city'
    return cache(url, () => get(url), 60 * 1000)
  }
  getServices () {
    const url = '/api/site/service'
    return cache(url, () => get(url), 60 * 60 * 1000)
  }
  getAuthStatus (force = false) {
    const url = '/auth/status'
    return cache(url, () => get(url), 60 * 1000, force)
  }
  login (username, password) {
    return post('/auth/login', { username, password }, { handleErrors: false })
  }
  changePassword (request) {
    return post('/auth/change-password', request)
  }
  // The user's own ready-made signature (Minu seaded -> Allkiri), prefilled as the
  // technician's signature on every act. Self-service only — there is no id in
  // any of these URLs, so nobody can touch someone else's.
  getMySignature () {
    return get('/auth/signature')
  }
  saveMySignature (signature) {
    return put('/auth/signature', { signature })
  }
  deleteMySignature () {
    return del('/auth/signature')
  }
}

export default new Api()